import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { S } from "./ibloom/styles";

/**
 * Why Now? — the "five stages" thesis page (Zero-to-One narrative for Mapbazar).
 *
 * Tells the story of how AI removes the offline-cataloguing bottleneck that's
 * kept 12M+ Indian shops invisible to the internet. Used as the investor /
 * shopkeeper conviction page; linked from the Login pane and /shopkeepers.
 */

const STAGES = [
  {
    era: "The Past",
    title: "The Cataloging Bottleneck",
    body: "Before AI, digitizing local inventory was fundamentally broken. It required expensive photoshoots, human models, and hours of manual data entry by shopkeepers who simply didn't have the time. The offline world stayed offline.",
    image: "https://customer-assets.emergentagent.com/job_212644d9-a926-47e4-b7c7-bf8862ff8173/artifacts/s0h6o29p_wholeScreenshot_6.png",
    accent: "#9A2C2C",
    badge: "rgba(154,44,44,0.12)",
    stats: [
      { v: "₹5,000+", k: "Cost per photoshoot" },
      { v: "2-3 weeks", k: "To catalog 100 products" },
      { v: "90%", k: "Shops still offline" },
    ],
  },
  {
    era: "The Present",
    title: "Meet 'ShopKeeper AI' — your AI Co-Pilot",
    body: "We aren't just handing shopkeepers a SaaS tool — we're giving them a personal AI manager. ShopKeeper AI lives on their phone, converses naturally, and instantly converts raw photos into e-commerce-ready catalogs. It completely eliminates the tech-literacy barrier for traditional Indian merchants.",
    image: "https://customer-assets.emergentagent.com/job_212644d9-a926-47e4-b7c7-bf8862ff8173/artifacts/r7ds4xcd_zerotoone.png",
    accent: "#C97B3C",
    badge: "rgba(201,123,60,0.14)",
    stats: [
      { v: "30 sec", k: "Photo → catalog" },
      { v: "₹0", k: "Cost to start" },
      { v: "0", k: "Tech skills needed" },
    ],
  },
  {
    era: "The Strategy",
    title: "Subsidizing supply to ignite growth",
    body: "Drop the marginal cost of cataloging to zero. Generative AI lets us offer AI image-generation to shopkeepers for free (or a nominal ₹20). This isn't just a pricing model — it's a deliberate strategy to rapidly aggregate the supply side of the market before anyone else can.",
    image: "https://customer-assets.emergentagent.com/job_212644d9-a926-47e4-b7c7-bf8862ff8173/artifacts/3pqwxn5r_zto1.png",
    accent: "#0F3D2E",
    badge: "rgba(15,61,46,0.12)",
    stats: [
      { v: "₹0-20", k: "Cost per listing" },
      { v: "Free", k: "AI model generation" },
      { v: "1st", k: "Mover advantage" },
    ],
  },
  {
    era: "The Moat",
    title: "The two-sided network effect",
    body: "Frictionless, free AI brings thousands of offline sellers online. This creates an unparalleled, structured database of hyper-local inventory. More sellers attract more buyers. More buyers lock in the sellers. This powerful network effect becomes our ultimate competitive moat.",
    image: "https://customer-assets.emergentagent.com/job_212644d9-a926-47e4-b7c7-bf8862ff8173/artifacts/x3kjb762_sport.png",
    accent: "#5E2A6C",
    badge: "rgba(94,42,108,0.12)",
    stats: [
      { v: "1000s", k: "Sellers onboarded" },
      { v: "2-sided", k: "Network effect" },
      { v: "Deep", k: "Competitive moat" },
    ],
  },
  {
    era: "The Future",
    title: "Dark Stores & AI-to-AI commerce",
    body: "Every local shop becomes a digitized Dark Store. Once offline inventory is structured on Mapbazar, it unlocks the ultimate frontier: an ecosystem where global buyers — and eventually autonomous AI purchasing agents — can instantly discover, compare, and buy from neighborhood merchants.",
    image: "https://customer-assets.emergentagent.com/job_212644d9-a926-47e4-b7c7-bf8862ff8173/artifacts/r7ds4xcd_zerotoone.png",
    accent: "#1C6B4A",
    badge: "rgba(28,107,74,0.14)",
    stats: [
      { v: "24/7", k: "AI agents buying" },
      { v: "Global", k: "Inventory access" },
      { v: "∞", k: "Scale potential" },
    ],
  },
];

const GALLERY = [
  { src: "https://customer-assets.emergentagent.com/job_212644d9-a926-47e4-b7c7-bf8862ff8173/artifacts/r7ds4xcd_zerotoone.png", label: "Fashion & Kurtis" },
  { src: "https://customer-assets.emergentagent.com/job_212644d9-a926-47e4-b7c7-bf8862ff8173/artifacts/3pqwxn5r_zto1.png", label: "Lifestyle" },
  { src: "https://customer-assets.emergentagent.com/job_212644d9-a926-47e4-b7c7-bf8862ff8173/artifacts/x3kjb762_sport.png", label: "Sports Equipment" },
  { src: "https://customer-assets.emergentagent.com/job_212644d9-a926-47e4-b7c7-bf8862ff8173/artifacts/s0h6o29p_wholeScreenshot_6.png", label: "Wholesale & Bulk" },
];

export default function WhyNow() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Why Now? · Mapbazar — The world's offline inventory is coming online";
    return () => { document.title = prev; };
  }, []);

  return (
    <div data-testid="why-now-page" style={{ background: "#0A1F18", color: "white", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", minHeight: "100dvh", overflowX: "hidden" }}>
      <TopNav />

      {/* HERO */}
      <section style={{ position: "relative", padding: "84px 22px 60px", textAlign: "center", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", top: -80, left: "50%", transform: "translateX(-50%)", width: 720, height: 720, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,213,74,0.12), transparent 65%)", filter: "blur(20px)" }} />
        <div style={{ maxWidth: 1080, margin: "0 auto", position: "relative" }}>
          <span style={{ display: "inline-block", padding: "6px 14px", borderRadius: 100, background: "rgba(255,213,74,0.16)", color: "#FFE7A6", fontSize: 11, fontWeight: 800, letterSpacing: 1.6, textTransform: "uppercase", marginBottom: 22 }} data-testid="why-now-eyebrow">
            ✨ Why Now?
          </span>
          <h1 style={{ ...S.fraunces, fontSize: "clamp(32px, 5.4vw, 60px)", fontWeight: 800, lineHeight: 1.05, letterSpacing: "-1.4px", margin: "0 0 18px" }} data-testid="why-now-h1">
            Shop local.<br />
            <span style={{ color: "#FFD54A" }}>Save on your family's health.</span>
          </h1>
          <p style={{ fontSize: "clamp(15px, 2vw, 18px)", lineHeight: 1.65, opacity: 0.88, maxWidth: 820, margin: "0 auto 30px" }}>
            MapBazzar was founded by <strong style={{ color: "#FFD54A" }}>Kirit Koladiya, a pharmacist</strong> who believes families shouldn't have to choose between daily expenses and health protection. Shop from your neighbourhood shops — and let your everyday purchases help pay for your family's <strong>health insurance</strong> and preventive health checkups.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/app" data-testid="why-now-hero-cta" style={primaryBtn}>📸 List your products free</Link>
            <Link to="/shopkeepers" data-testid="why-now-secondary-cta" style={ghostBtn}>For sellers →</Link>
          </div>
        </div>
      </section>

      {/* THE FIVE STAGES */}
      <section style={{ padding: "60px 22px", background: "linear-gradient(180deg, #0A1F18 0%, #0F2A20 100%)" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <SectionEyebrow>The Five Stages</SectionEyebrow>
          <SectionTitle>From bottleneck to unstoppable moat</SectionTitle>

          <div style={{ marginTop: 50, display: "flex", flexDirection: "column", gap: 28 }} data-testid="why-now-stages">
            {STAGES.map((s, i) => (
              <StageCard key={i} index={i + 1} stage={s} reverse={i % 2 === 1} />
            ))}
          </div>
        </div>
      </section>

      {/* SEE IT IN ACTION — gallery */}
      <section style={{ padding: "60px 22px", background: "#0A1F18" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", textAlign: "center" }}>
          <SectionEyebrow>See It In Action</SectionEyebrow>
          <SectionTitle>From messy shelf to shoppable catalog</SectionTitle>
          <p style={{ fontSize: 15, opacity: 0.78, maxWidth: 640, margin: "12px auto 32px", lineHeight: 1.6 }}>
            Real transformations. Raw smartphone photos turned into professional product listings by AI in under 30 seconds.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }} data-testid="why-now-gallery">
            {GALLERY.map((g, i) => (
              <div key={i} style={{ borderRadius: 18, overflow: "hidden", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ width: "100%", aspectRatio: "4/5", background: "#0F2A20", overflow: "hidden" }}>
                  <img src={g.src} alt={g.label} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </div>
                <div style={{ padding: "12px 14px", textAlign: "left" }}>
                  <div style={{ fontSize: 9.5, letterSpacing: 1.5, color: "#FFD54A", fontWeight: 800, marginBottom: 4 }}>✨ AI TRANSFORMED</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>{g.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE BIG PICTURE */}
      <section style={{ padding: "70px 22px", background: "linear-gradient(180deg, #0F2A20 0%, #0A1F18 100%)" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ textAlign: "center" }}>
            <SectionEyebrow>The Big Picture</SectionEyebrow>
            <h2 style={{ ...S.fraunces, fontSize: "clamp(26px, 4.2vw, 42px)", fontWeight: 800, lineHeight: 1.15, letterSpacing: "-1px", margin: "12px auto 22px", maxWidth: 880 }}>
              We're not just building a marketplace.<br />
              We're building <span style={{ color: "#FFD54A" }}>the infrastructure for AI-to-AI commerce.</span>
            </h2>
          </div>
          <div style={{ marginTop: 36, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
            <BigPicCard emoji="🧙‍♂️" title="For Shopkeepers" body="Snap a photo, AI creates the listing. Your offline shop becomes a searchable, shoppable digital store — in seconds. No tech skills needed." />
            <BigPicCard emoji="🛍️" title="For Buyers" body="Discover products from shops near you. Scan any product to find it locally. Order via WhatsApp. Get it delivered from 2 km away — not 2,000 km." />
            <BigPicCard emoji="🛵" title="For Delivery" body="Hyper-local delivery within 5 km. Faster than any ecommerce platform. Lower costs. Zero warehouse inventory needed." />
            <BigPicCard emoji="🤖" title="For AI Agents" body="Structured, real-time local inventory data. AI buying agents can crawl, compare prices, and execute purchases autonomously across thousands of shops." />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ padding: "70px 22px 90px", background: "#0A1F18" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ ...S.fraunces, fontSize: "clamp(26px, 4.2vw, 40px)", fontWeight: 800, letterSpacing: "-0.8px", margin: "0 0 14px" }}>
            Ready to join the <span style={{ color: "#FFD54A" }}>revolution?</span>
          </h2>
          <p style={{ fontSize: 15.5, opacity: 0.85, lineHeight: 1.6, margin: "0 0 28px" }}>
            List your first product in 30 seconds. No signup required. Just snap a photo and let AI do the rest.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/app" data-testid="why-now-final-cta" style={{ ...primaryBtn, padding: "16px 28px", fontSize: 15 }}>🚀 Start listing free</Link>
            <Link to="/shopkeepers" data-testid="why-now-learn-more" style={ghostBtn}>Learn more for sellers</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ===== Components ===== */

function StageCard({ index, stage, reverse }) {
  return (
    <div data-testid={`why-now-stage-${index}`} style={{
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr)",
      gap: 22,
      borderRadius: 22,
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.08)",
      padding: 22,
      overflow: "hidden",
    }} className="why-stage-card">
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 22 }}>
        <div style={{ display: "flex", flexDirection: reverse ? "row-reverse" : "row", gap: 24, flexWrap: "wrap", alignItems: "center" }} className="why-stage-row">
          {/* Image */}
          <div style={{ flex: "1 1 280px", borderRadius: 16, overflow: "hidden", aspectRatio: "4/3", background: "#0F2A20", border: "1px solid rgba(255,255,255,0.06)" }}>
            <img src={stage.image} alt={stage.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </div>
          {/* Copy */}
          <div style={{ flex: "1 1 320px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ width: 30, height: 30, borderRadius: "50%", background: stage.accent, color: "white", fontWeight: 900, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>{index}</span>
              <span style={{ padding: "4px 10px", borderRadius: 100, background: stage.badge, color: stage.accent === "#9A2C2C" ? "#FFB4B4" : stage.accent === "#C97B3C" ? "#FFE7A6" : stage.accent === "#5E2A6C" ? "#E7B6F0" : "#A6E5C2", fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase" }}>{stage.era}</span>
            </div>
            <h3 style={{ ...S.fraunces, fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.5px", margin: "0 0 12px" }}>{stage.title}</h3>
            <p style={{ fontSize: 14.5, lineHeight: 1.65, opacity: 0.88, margin: "0 0 18px" }}>{stage.body}</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              {stage.stats.map((s, i) => (
                <div key={i} style={{ padding: "10px 8px", borderRadius: 12, background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>
                  <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 800, color: "#FFD54A", letterSpacing: "-0.3px" }}>{s.v}</div>
                  <div style={{ fontSize: 10.5, opacity: 0.72, marginTop: 2, lineHeight: 1.3 }}>{s.k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BigPicCard({ emoji, title, body }) {
  return (
    <div style={{ padding: 22, borderRadius: 18, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ fontSize: 28, marginBottom: 10 }}>{emoji}</div>
      <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 800, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13.5, opacity: 0.85, lineHeight: 1.6 }}>{body}</div>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return <div style={{ fontSize: 10.5, letterSpacing: 1.8, color: "#FFE7A6", fontWeight: 800, textTransform: "uppercase", textAlign: "center" }}>{children}</div>;
}

function SectionTitle({ children }) {
  return <h2 style={{ ...S.fraunces, fontSize: "clamp(26px, 4.2vw, 40px)", fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.8px", margin: "10px auto 0", textAlign: "center", maxWidth: 820 }}>{children}</h2>;
}

function TopNav() {
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 80, background: "rgba(10,31,24,0.85)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "white" }} data-testid="why-now-logo">
          <img src="/icon-192.png" alt="Mapbazar" width={32} height={32} style={{ borderRadius: 9, display: "block", background: "white" }} />
          <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 700, letterSpacing: "-0.4px" }}>Mapbazar</div>
        </Link>
        <Link to="/app" style={{ padding: "8px 16px", borderRadius: 100, background: "#FFD54A", color: "#0A1F18", fontSize: 12.5, fontWeight: 800, textDecoration: "none" }} data-testid="why-now-nav-cta">List free →</Link>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer style={{ padding: "28px 22px 36px", textAlign: "center", fontSize: 12, opacity: 0.6, background: "#0A1F18", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      © {new Date().getFullYear()} Mapbazar · Built in India · Unlocking offline inventory with AI
    </footer>
  );
}

const primaryBtn = {
  display: "inline-block", padding: "14px 26px", borderRadius: 100,
  background: "#FFD54A", color: "#0A1F18", fontWeight: 800, fontSize: 14,
  textDecoration: "none", boxShadow: "0 10px 28px rgba(255,213,74,0.32)",
};

const ghostBtn = {
  display: "inline-block", padding: "14px 22px", borderRadius: 100,
  background: "rgba(255,255,255,0.06)", color: "white",
  border: "1px solid rgba(255,255,255,0.16)", fontWeight: 700, fontSize: 13.5,
  textDecoration: "none",
};
